// servizi/numeriTelefono.ts — v046: funzioni pure per «Invita dalla rubrica» (servizi/contatti.ts): numeri di telefono
// in forma internazionale e link per aprire il messaggio già scritto (SMS o WhatsApp). Nessun import: si provano con
// node --test senza le librerie dell'app (numeriTelefono.test.ts).

/**
 * Prefisso internazionale per paese (codice ISO della regione della lingua del telefono, es. «it-IT» → IT). Serve
 * solo per i numeri salvati in rubrica senza «+»: per i paesi che non sono qui il numero resta com'è.
 */
const PREFISSI: Record<string, string> = {
  IT: "39", SM: "378", VA: "39", CH: "41", ES: "34", FR: "33", MC: "377", BE: "32", NL: "31", LU: "352", DE: "49",
  AT: "43", GB: "44", IE: "353", PT: "351", PL: "48", RO: "40", GR: "30", HR: "385", SI: "386", AL: "355",
  SE: "46", NO: "47", DK: "45", FI: "358", CZ: "420", SK: "421", HU: "36", BG: "359", RS: "381", UA: "380",
  TR: "90", RU: "7", US: "1", CA: "1", MX: "52", BR: "55", AR: "54", CL: "56", CO: "57", PE: "51", VE: "58",
  IN: "91", PK: "92", BD: "880", CN: "86", JP: "81", KR: "82", ID: "62", PH: "63", VN: "84", TH: "66", MY: "60",
  AU: "61", NZ: "64", ZA: "27", NG: "234", EG: "20", MA: "212", TN: "216", DZ: "213", SA: "966", AE: "971",
  IL: "972",
};
/** Paesi in cui lo 0 iniziale fa parte del numero anche dall'estero (Italia: +39 06 …). Negli altri lo 0 si toglie. */
const ZERO_RESTA = new Set(["IT", "SM", "VA"]);

/** La regione da un codice lingua («it-IT», «en_US», «pt-BR») in maiuscolo, o "" se non c'è. */
export function regioneDaLingua(lingua: string | undefined | null): string {
  const m = /^[a-z]{2,3}(?:[-_][A-Za-z]{4})?[-_]([A-Za-z]{2})\b/.exec(String(lingua ?? "").trim());
  return m ? m[1]!.toUpperCase() : "";
}

/**
 * Un numero in forma internazionale «+<cifre>» (o solo cifre se il paese non si conosce). `regione` (es. "IT") serve
 * per i numeri senza «+» né «00». Restituisce null per ciò che non è un numero di telefono (troppo corto o lungo).
 */
export function normalizzaNumero(numero: string, regione = ""): string | null {
  let s = String(numero ?? "").trim();
  // interno («123 456 int. 7», «x7», «;ext=7», «,7» pausa di Android): si tiene solo il numero principale
  s = s.replace(/\s*(?:;\s*ext\s*=|[;,#]|ext\.?|int\.?|x)\s*\d+\s*$/i, "");
  const piu = /^\s*\+/.test(s);
  let cifre = s.replace(/[^\d]/g, "");
  if (!cifre) return null;
  if (piu) return lunghezzaBuona(cifre) ? `+${cifre}` : null;
  if (cifre.startsWith("00")) {
    cifre = cifre.slice(2);
    return lunghezzaBuona(cifre) ? `+${cifre}` : null;
  }
  const r = regione.toUpperCase();
  const prefisso = PREFISSI[r];
  if (!prefisso) return lunghezzaBuona(cifre, 5) ? cifre : null;
  // Nord America: «1 555 123 4567» ha già il prefisso del paese
  if (prefisso === "1" && cifre.length === 11 && cifre.startsWith("1")) return `+${cifre}`;
  // numero scritto con il prefisso del paese ma senza «+» (es. «39 333 1234567» in Italia): si riconosce dalla lunghezza
  if (cifre.startsWith(prefisso) && cifre.length >= prefisso.length + 9 && cifre.length > 10) return `+${cifre}`;
  if (!ZERO_RESTA.has(r) && cifre.startsWith("0")) cifre = cifre.replace(/^0+/, "");
  const tutto = prefisso + cifre;
  return lunghezzaBuona(tutto) && cifre.length >= 5 ? `+${tutto}` : null;
}

/** E.164: al massimo 15 cifre; meno di 7 non è un numero da invitare (numeri brevi, servizi). */
const lunghezzaBuona = (cifre: string, minimo = 7): boolean => cifre.length >= minimo && cifre.length <= 15;

/** I numeri di un contatto, normalizzati, senza doppioni e senza quelli non validi (nell'ordine della rubrica). */
export function normalizzaNumeri(numeri: readonly (string | null | undefined)[], regione = ""): string[] {
  const visti = new Set<string>();
  for (const n of numeri) {
    const x = n ? normalizzaNumero(n, regione) : null;
    if (x) visti.add(x);
  }
  return [...visti];
}

export type SistemaSms = "ios" | "android";

/**
 * Link «sms:» con il messaggio già scritto. Le due piattaforme vogliono il corpo in modo diverso:
 * Android «sms:+39…,+39…?body=…»; iOS «sms:+39…&body=…» (un numero) o «sms:/open?addresses=+39…,+39…&body=…» (più
 * numeri: è la sola forma con cui Messaggi li apre tutti). Senza numeri si apre il messaggio senza destinatario.
 */
export function linkSms(numeri: readonly string[], testo: string, sistema: SistemaSms): string {
  const a = numeri.map((n) => n.replace(/[^\d+]/g, "")).filter(Boolean);
  const corpo = encodeURIComponent(testo);
  if (sistema === "ios") {
    if (a.length > 1) return `sms:/open?addresses=${a.join(",")}&body=${corpo}`;
    return `sms:${a[0] ?? ""}&body=${corpo}`;
  }
  return `sms:${a.join(",")}?body=${corpo}`;
}

/**
 * Link di WhatsApp con il messaggio già scritto (wa.me: apre l'app se c'è, altrimenti la pagina di WhatsApp). Il
 * numero va in forma internazionale senza «+» né zeri davanti; senza numero si sceglie la chat in WhatsApp.
 */
export function linkWa(numero: string, testo: string): string {
  const cifre = String(numero ?? "").replace(/^\s*00/, "").replace(/\D/g, "");
  return `https://wa.me/${cifre}?text=${encodeURIComponent(testo)}`;
}
